import type { ReactNode } from 'react';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';

type FilterBarProps = {
  /** Filter components (LocationFilter, CategoryFilter). The wrapper only
   *  handles layout — the children own their popovers and the shared filter
   *  state passed by the route. */
  children: ReactNode;
  /** Number of filters being rendered. When exactly one filter is shown the
   *  bar shrinks to a narrower max-width so a lone trigger doesn't stretch
   *  uncomfortably wide across the layout. Ignored when `fullWidth` is set —
   *  a caller asking to fill its container means it. */
  filterCount: number;
  /**
   * Fill the container instead of capping the bar and centring it.
   *
   * Default (`false`) suits a bar centred above a narrower column, like the
   * home page's. Set this where the bar heads content that already spans the
   * full width — the upcoming-events grid, say — so its edges line up with
   * what it filters instead of floating in the middle.
   */
  fullWidth?: boolean;
};

/**
 * Inline desktop search bar. One raised surface holding filter triggers
 * separated by thin vertical dividers — the bar reads as a single unified
 * control, not several independent input fields.
 *
 * Children should be filter triggers that render their own button + label +
 * value internally with NO surrounding border or background (the bar owns
 * the chrome). Dividers between adjacent children are drawn via a left
 * border on every child except the first.
 *
 * Mobile uses `<FilterSheet>` instead — this component should be wrapped
 * in a `display: { base: 'none', tablet: 'flex' }` toggle by the route.
 */
export function FilterBar({
  children,
  filterCount,
  fullWidth = false,
}: FilterBarProps) {
  // Exactly ONE of the three width classes, always — see the note on
  // `barStyles` about competing `maxWidth` rules.
  const widthStyles = fullWidth
    ? fullStyles
    : filterCount <= 1
      ? narrowStyles
      : wideStyles;

  return <div className={cx(barStyles, widthStyles)}>{children}</div>;
}

const barStyles = css({
  display: 'grid',
  // Auto-fit so the bar adapts to however many filter children the route
  // renders — Location and Category are hidden when their option list has
  // ≤1 entry.
  gridAutoColumns: '[minmax(0, 1fr)]',
  gridAutoFlow: 'column',
  width: 'full',
  // `marginInline: auto` keeps the bar centred independently of the parent
  // layout. The max-width cap lives in wideStyles/narrowStyles — exactly one
  // of the two is applied, never both. Putting a maxWidth here too would race
  // it against the narrow cap: with equal specificity the winner is decided
  // by atomic-rule order in the generated stylesheet, not by `cx` order
  // (which is how US-10265's 1000px cap silently disabled the 400px
  // narrowing — US-10454).
  marginInline: 'auto',
  background: 'surface.raised',
  borderRadius: 'large',
  border: 'small',
  borderColor: 'border.interactive',
  shadow: 'medium',
  // xsmall (8px) top/bottom padding keeps the bar tight: 8 + 48px trigger + 8
  // = a 64px content box (66px rendered incl. the 1px top/bottom border).
  padding: 'xsmall',
  // Each immediate child after the first gets a left border, creating the
  // vertical dividers between fields without adding wrapper elements.
  '& > *:not(:first-child)': {
    borderLeft: 'small',
    borderColor: 'border.neutral',
  },
});

// Cap the bar so it doesn't stretch the full content width on wide viewports.
const wideStyles = css({
  maxWidth: '[1000px]',
});

// Tighten the bar when only one filter is shown — a single trigger stretched
// across the full content width looks accidental rather than intentional.
const narrowStyles = css({
  maxWidth: '[400px]',
});

// `fullWidth`: no cap, so the bar fills whatever container it's placed in.
// Still a `maxWidth` (rather than simply omitting one) so it stays mutually
// exclusive with the two caps above — see the note on `barStyles`.
const fullStyles = css({
  maxWidth: 'full',
});
